import { useOrganization, useOrganizationList } from '@clerk/react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { useAuth } from 'src/auth';
import NavigationBlocker from 'src/components/containers/NavigationBlocker';
import { buildAppHref } from 'src/lib/routing';
import AiChatDrawer from 'src/modules/ai/components/AiChatDrawer';
import { useAiInboxOnlyMode } from 'src/modules/ai-inbox/hooks/useAiInboxOnlyMode';
import SiteHeader, { GLOBAL_APP_BAR_HEIGHT } from 'src/modules/pageFrame/SiteHeader';
import { useAlgolia } from 'src/providers/AlgoliaProvider';
import EditingStateProvider from 'src/providers/EditingStateProvider';
import GridFocusProvider from 'src/providers/GridFocusProvider';
import SidebarContextProvider from 'src/providers/SidebarContextProvider';
import { appLinks } from 'src/routing/appLinks';
import SideNavigation from './SideNavigation';

export default function AgrifulFrame({ children }: React.PropsWithChildren<{}>) {
  const { setActive, userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList({ userMemberships: true });
  const { organization } = useOrganization();
  const { currentUser } = useAuth();
  const { locked, isAllowedPath } = useAiInboxOnlyMode();

  const algolia = useAlgolia();
  const router = useRouter();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    algolia?.clearCache?.();
  }, [algolia, pathname]);

  useEffect(() => {
    if (!currentUser) return;
    if (organization) return;
    if (!isOrgListLoaded) return;

    if (userMemberships?.data?.length > 0) {
      setActive({ organization: userMemberships.data[0].organization.id });
      return;
    }

    if (pathname === buildAppHref(router, appLinks.notAMember())) return;

    void navigate(appLinks.notAMember());
  }, [setActive, userMemberships, isOrgListLoaded, pathname, organization, currentUser, navigate, router]);

  // AI Inbox-only lockdown: when the `ai-inbox-only` flag is on for this org,
  // bounce the user back to /ai-inbox if they land anywhere outside the
  // allow list (set by useAiInboxOnlyMode — /ai-inbox + /user-profile).
  const shouldRedirectForLockdown = locked && !isAllowedPath(pathname);
  useEffect(() => {
    if (!shouldRedirectForLockdown) return;
    void navigate(appLinks.aiInbox());
  }, [shouldRedirectForLockdown, navigate]);

  // While the redirect is in flight (one tick), render nothing so the frame
  // chrome (header, sidebar, LandingPage welcome cell, etc.) doesn't flash.
  if (shouldRedirectForLockdown) return null;

  return (
    <EditingStateProvider>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <SnackbarProvider maxSnack={5} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <NavigationBlocker />
          <SidebarContextProvider>
            <SiteHeader />
            <SideNavigation />
            <AiChatDrawer />

            <GridFocusProvider>
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  pt: `${GLOBAL_APP_BAR_HEIGHT}px`,
                  minHeight: '100vh',
                  flexDirection: 'column',
                }}
              >
                {children}
              </Box>
            </GridFocusProvider>
          </SidebarContextProvider>
        </SnackbarProvider>
      </Box>
    </EditingStateProvider>
  );
}
